/**
 * График безубыточности (блок 3 формы): выручка, постоянные, переменные,
 * общие затраты и результат при загрузке 20…260 ч в месяц, тыс. ₽. SVG руками:
 * одна ось значений, тонкие линии, подписи концов, вертикали «безубыточность»
 * и «план», перекрестие по наведению и с клавиатуры. Рядом — та же таблица.
 */
import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import styled, { useTheme } from 'styled-components';
import type { PrcBreakEven } from '@shared/api';
import { ScrollRegion, VisuallyHidden, fleetType } from '@entities/fleet';
import { prcChartColors } from '../chartTokens';
import { formatHours, formatNumber } from '../format';
import { NARROW, NumTable } from '../ui';

type SeriesKey = 'revenue' | 'total' | 'result' | 'fixed' | 'variable';

interface Series {
  key: SeriesKey;
  label: string;
  emphasis: boolean;
}

const SERIES: readonly Series[] = [
  { key: 'revenue', label: 'Выручка', emphasis: true },
  { key: 'total', label: 'Общие затраты', emphasis: true },
  { key: 'result', label: 'Результат', emphasis: true },
  { key: 'fixed', label: 'Постоянные (FC)', emphasis: false },
  { key: 'variable', label: 'Переменные (VC × Q)', emphasis: false },
];

/**
 * Рамка графика в пикселях экрана: viewBox равен ширине блока, поэтому
 * подписи осей не уменьшаются вместе с графиком на телефоне.
 */
interface Frame {
  W: number;
  H: number;
  M: { left: number; right: number; top: number; bottom: number };
  plotW: number;
  plotH: number;
  /** Узкий блок: без подписей концов линий (их место — легенда над графиком). */
  compact: boolean;
}

const DEFAULT_WIDTH = 640;
const COMPACT_BELOW = 480;

function frameFor(width: number): Frame {
  const compact = width < COMPACT_BELOW;
  const W = Math.max(260, Math.round(width));
  const H = compact ? 260 : 320;
  const M = compact
    ? { left: 44, right: 14, top: 30, bottom: 40 }
    : { left: 56, right: 132, top: 28, bottom: 40 };
  return { W, H, M, plotW: W - M.left - M.right, plotH: H - M.top - M.bottom, compact };
}

/** Ширина блока: замер до отрисовки и ResizeObserver на изменения. */
function useBoxWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setWidth((prev) => (Math.abs(prev - w) >= 1 ? w : prev));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(300px, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @container prc (max-width: 1040px) {
    grid-template-columns: minmax(0, 1fr);
  }

  /* Альбомный лист (~1030 px) — график и таблица рядом, на одной странице. */
  @media print {
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  }
`;

const Figure = styled.figure`
  position: relative;
  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  &:focus-visible {
    border-radius: ${({ theme }) => theme.radius.md};
  }

  @media print {
    break-inside: avoid;
  }
`;

const PlotBox = styled.div`
  & > svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
    touch-action: pan-y;
  }
`;

const Legend = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  padding: 0;
  list-style: none;
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};

  & > li {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
  }
`;

/** Образец линии: рамкой, а не фоном — фон при печати по умолчанию не выводится. */
const Key = styled.span<{ $color: string; $thin: boolean }>`
  flex: none;
  width: 16px;
  height: 0;
  border-top: ${({ $thin, $color }) => `${$thin ? '1.5px' : '2px'} solid ${$color}`};
`;

const Caption = styled.figcaption`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Tip = styled.div<{ $left: number; $flip: boolean }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing(10)};
  left: ${({ $left }) => `${$left}%`};
  z-index: 2;
  min-width: 188px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
  font-size: ${fleetType.small};
  pointer-events: none;
  transform: ${({ $flip, theme }) =>
    $flip ? `translateX(calc(-100% - ${theme.spacing(3)}))` : `translateX(${theme.spacing(3)})`};

  & > .q {
    margin: 0 0 ${({ theme }) => theme.spacing(1)};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & dl {
    display: grid;
    grid-template-columns: auto auto;
    gap: 2px ${({ theme }) => theme.spacing(3)};
    margin: 0;
  }

  & dt {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & dd {
    margin: 0;
    font-weight: 600;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  @container prc (max-width: ${NARROW}) {
    display: none;
  }

  @media print {
    display: none;
  }
`;

/** Пара «подпись — значение» подсказки: участвует в сетке списка. */
const Pair = styled.div`
  display: contents;
`;

const DataTable = styled(NumTable)`
  width: auto;
  min-width: min(560px, 100%);

  tbody tr[data-active='true'] > td,
  tbody tr[data-active='true'] > th {
    background: ${({ theme }) => theme.colors.surface};
  }

  tbody tr[data-mark='true'] > th {
    font-weight: 600;
  }

  /* Телефон: плотнее по горизонтали — часы, выручка, общие и результат
     помещаются без прокрутки, FC и VC × Q — прокруткой вбок. */
  @container prc (max-width: ${NARROW}) {
    thead th,
    tbody th,
    td {
      padding-left: ${({ theme }) => theme.spacing(1.5)};
      padding-right: ${({ theme }) => theme.spacing(1.5)};
    }
  }

  @media print {
    font-size: 9pt;
  }
`;

function niceStep(range: number, target: number): number {
  const raw = range / Math.max(1, target);
  const pow = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / pow;
  const nice = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10;
  return nice * pow;
}

interface Point {
  hours: number;
  values: Record<SeriesKey, number>;
}

/**
 * Числа графика — только координаты на экране: строки сервера переводятся в
 * числа для геометрии, подписи и таблица показывают сами строки.
 */
function toPoints(chart: PrcBreakEven['chart']): Point[] {
  return chart.map((c) => ({
    hours: c.hours,
    values: {
      revenue: Number(c.revenue),
      total: Number(c.total),
      result: Number(c.result),
      fixed: Number(c.fixed),
      variable: Number(c.variable),
    },
  }));
}

export function BreakEvenChart({ data }: { data: PrcBreakEven }) {
  const theme = useTheme();
  const colors = prcChartColors(theme);
  const uid = useId();
  const [active, setActive] = useState<number | null>(null);
  const points = useMemo(() => toPoints(data.chart), [data.chart]);
  const qBep = data.result.q_bep !== null ? Number(data.result.q_bep) : null;
  const qPlan = Number(data.inputs.q_plan);
  const boxRef = useRef<HTMLDivElement>(null);
  const width = useBoxWidth(boxRef);
  const frame = useMemo(() => frameFor(width), [width]);
  const { W, H, M, plotW: PLOT_W, plotH: PLOT_H, compact } = frame;

  const colorOf = (key: SeriesKey): string =>
    key === 'revenue'
      ? colors.revenue
      : key === 'total'
        ? colors.total
        : key === 'result'
          ? colors.result
          : theme.colors.text.muted;

  const geometry = useMemo(() => {
    const xs = points.map((p) => p.hours);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const all = points.flatMap((p) => SERIES.map((s) => p.values[s.key]));
    let lo = Math.min(0, ...all);
    let hi = Math.max(0, ...all);
    if (lo === hi) hi = lo + 1;
    const step = niceStep(hi - lo, 5);
    lo = Math.floor(lo / step) * step;
    hi = Math.ceil(hi / step) * step;
    const ticks: number[] = [];
    for (let t = lo; t <= hi + step / 2; t += step) ticks.push(Number(t.toFixed(6)));
    const x = (h: number) => frame.M.left + ((h - minX) / (maxX - minX || 1)) * frame.plotW;
    const y = (val: number) => frame.M.top + (1 - (val - lo) / (hi - lo)) * frame.plotH;
    return { minX, maxX, lo, hi, ticks, x, y };
  }, [points, frame]);

  const { x, y, ticks, minX, maxX } = geometry;
  const last = points[points.length - 1];

  // Подписи концов линий: по вертикали без наложений, со сноской к линии.
  const endLabels = useMemo(() => {
    if (!last) return [];
    const raw = SERIES.map((s) => ({ s, y0: y(last.values[s.key]) })).sort((a, b) => a.y0 - b.y0);
    const gap = 15;
    const placed: { s: Series; y0: number; y1: number }[] = [];
    for (const r of raw) {
      const prev = placed[placed.length - 1];
      const y1 = prev ? Math.max(r.y0, prev.y1 + gap) : r.y0;
      placed.push({ ...r, y1 });
    }
    // Ушли ниже графика — сдвигаем всю стопку вверх.
    const overflow = (placed[placed.length - 1]?.y1 ?? 0) - (frame.M.top + frame.plotH + 8);
    if (overflow > 0) for (const p of placed) p.y1 -= overflow;
    return placed;
  }, [last, y, frame]);

  const xTicks = points.filter((_, i) => i % (compact ? 3 : 2) === 0).map((p) => p.hours);

  /** Подпись вертикали — справа от линии, у правого края — слева: не вылезает за график. */
  const sideLabel = (px: number, text: string): { x: number; anchor: 'start' | 'end' } =>
    px + 4 + text.length * 6.2 > W - 2
      ? { x: px - 4, anchor: 'end' }
      : { x: px + 4, anchor: 'start' };
  const bepText = qBep !== null ? `безубыточность ${formatHours(data.result.q_bep, 0)}` : '';
  const planText = `план ${formatHours(data.inputs.q_plan)}`;
  const activePoint = active !== null ? points[active] : undefined;

  function pick(clientX: number, target: SVGSVGElement) {
    const rect = target.getBoundingClientRect();
    const px = ((clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestD = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(x(p.hours) - px);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    setActive(best);
  }

  function onKey(e: KeyboardEvent<HTMLElement>) {
    const lastIndex = points.length - 1;
    const cur = active ?? 0;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = Math.min(lastIndex, cur + 1);
    else if (e.key === 'ArrowLeft') next = Math.max(0, cur - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = lastIndex;
    else if (e.key === 'Escape') {
      setActive(null);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  }

  const announce = activePoint
    ? `${activePoint.hours} ч в месяц: ${SERIES.map((s) => `${s.label} ${formatNumber(data.chart[active ?? 0]?.[s.key], 1)} тыс. ₽`).join(', ')}`
    : '';

  return (
    <Layout>
      <Figure
        tabIndex={0}
        aria-labelledby={`${uid}-cap`}
        aria-describedby={`${uid}-help`}
        onKeyDown={onKey}
        onFocus={() =>
          setActive(
            (a) =>
              a ??
              Math.max(
                0,
                points.findIndex((p) => p.hours >= qPlan),
              ),
          )
        }
        onBlur={() => setActive(null)}
      >
        <Legend aria-hidden="true">
          {SERIES.map((s) => (
            <li key={s.key}>
              <Key $color={colorOf(s.key)} $thin={!s.emphasis} />
              {s.label}
            </li>
          ))}
        </Legend>
        <PlotBox ref={boxRef}>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-labelledby={`${uid}-cap`}
            onPointerMove={(e: PointerEvent<SVGSVGElement>) => pick(e.clientX, e.currentTarget)}
            onPointerLeave={() => setActive(null)}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={M.left}
                  x2={M.left + PLOT_W}
                  y1={y(t)}
                  y2={y(t)}
                  stroke={t === 0 ? theme.colors.borderStrong : theme.colors.border}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  x={M.left - 8}
                  y={y(t)}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={11}
                  fill={theme.colors.text.secondary}
                >
                  {formatNumber(String(t), 1)}
                </text>
              </g>
            ))}
            <text
              x={M.left - 8}
              y={M.top - 14}
              textAnchor="end"
              fontSize={11}
              fill={theme.colors.text.secondary}
            >
              тыс. ₽
            </text>
            {xTicks.map((h) => (
              <text
                key={h}
                x={x(h)}
                y={M.top + PLOT_H + 18}
                textAnchor="middle"
                fontSize={11}
                fill={theme.colors.text.secondary}
              >
                {h}
              </text>
            ))}
            <text
              x={M.left + PLOT_W}
              y={M.top + PLOT_H + 34}
              textAnchor="end"
              fontSize={11}
              fill={theme.colors.text.secondary}
            >
              ч в месяц
            </text>

            {qBep !== null && qBep >= minX && qBep <= maxX && (
              <g>
                <line
                  x1={x(qBep)}
                  x2={x(qBep)}
                  y1={M.top}
                  y2={M.top + PLOT_H}
                  stroke={theme.colors.text.secondary}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  x={sideLabel(x(qBep), bepText).x}
                  y={M.top - 6}
                  textAnchor={sideLabel(x(qBep), bepText).anchor}
                  fontSize={11}
                  fill={theme.colors.text.primary}
                >
                  {bepText}
                </text>
              </g>
            )}
            {qPlan >= minX && qPlan <= maxX && (
              <g>
                <line
                  x1={x(qPlan)}
                  x2={x(qPlan)}
                  y1={M.top + 14}
                  y2={M.top + PLOT_H}
                  stroke={theme.colors.primary}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text
                  x={sideLabel(x(qPlan), planText).x}
                  y={M.top + 10}
                  textAnchor={sideLabel(x(qPlan), planText).anchor}
                  fontSize={11}
                  fill={theme.colors.text.primary}
                >
                  {planText}
                </text>
              </g>
            )}

            {[...SERIES].reverse().map((s) => (
              <polyline
                key={s.key}
                points={points.map((p) => `${x(p.hours)},${y(p.values[s.key])}`).join(' ')}
                fill="none"
                stroke={colorOf(s.key)}
                strokeWidth={s.emphasis ? 2 : 1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}

            {last &&
              !compact &&
              endLabels.map((l) => (
                <g key={l.s.key}>
                  {Math.abs(l.y1 - l.y0) > 2 && (
                    <line
                      x1={x(last.hours) + 2}
                      y1={l.y0}
                      x2={x(last.hours) + 10}
                      y2={l.y1}
                      stroke={theme.colors.border}
                      strokeWidth={1}
                    />
                  )}
                  <circle
                    cx={x(last.hours)}
                    cy={l.y0}
                    r={3.5}
                    fill={colorOf(l.s.key)}
                    stroke={theme.colors.backgroundElevated}
                    strokeWidth={2}
                  />
                  <text
                    x={x(last.hours) + 12}
                    y={l.y1}
                    dominantBaseline="middle"
                    fontSize={11}
                    fill={theme.colors.text.primary}
                  >
                    {l.s.label.replace(/ \(.*\)$/, '')}
                  </text>
                </g>
              ))}

            {activePoint && (
              <g>
                <line
                  x1={x(activePoint.hours)}
                  x2={x(activePoint.hours)}
                  y1={M.top}
                  y2={M.top + PLOT_H}
                  stroke={theme.colors.text.secondary}
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                {SERIES.map((s) => (
                  <circle
                    key={s.key}
                    cx={x(activePoint.hours)}
                    cy={y(activePoint.values[s.key])}
                    r={4}
                    fill={colorOf(s.key)}
                    stroke={theme.colors.backgroundElevated}
                    strokeWidth={2}
                  />
                ))}
              </g>
            )}
            <rect x={M.left} y={M.top} width={PLOT_W} height={PLOT_H} fill="transparent" />
          </svg>
        </PlotBox>
        {activePoint && active !== null && (
          <Tip
            $left={Math.round((x(activePoint.hours) / W) * 100)}
            $flip={active > points.length / 2}
            aria-hidden="true"
          >
            <p className="q">{activePoint.hours} ч в месяц, тыс. ₽</p>
            <dl>
              {SERIES.map((s) => (
                <Pair key={s.key}>
                  <dt>
                    <Key $color={colorOf(s.key)} $thin={!s.emphasis} />
                    {s.label}
                  </dt>
                  <dd>{formatNumber(data.chart[active]?.[s.key], 1)}</dd>
                </Pair>
              ))}
            </dl>
          </Tip>
        )}
        <Caption id={`${uid}-cap`}>
          График безубыточности: выручка и затраты при загрузке от 20 до 260 ч в месяц, тыс. ₽
          {qBep === null ? '; безубыточности нет при любой загрузке' : ''}.
        </Caption>
        <VisuallyHidden id={`${uid}-help`}>
          Стрелки влево и вправо — значения по часам; те же числа в таблице рядом.
        </VisuallyHidden>
        <VisuallyHidden aria-live="polite">{announce}</VisuallyHidden>
      </Figure>

      <ScrollRegion label="Таблица графика безубыточности">
        <DataTable>
          <caption>
            <VisuallyHidden>Значения графика безубыточности, тыс. ₽</VisuallyHidden>
          </caption>
          <thead>
            <tr>
              <th scope="col">Ч в мес.</th>
              <th scope="col" className="num">
                Выручка
              </th>
              <th scope="col" className="num">
                Общие
              </th>
              <th scope="col" className="num">
                Результат
              </th>
              <th scope="col" className="num">
                FC
              </th>
              <th scope="col" className="num">
                VC × Q
              </th>
            </tr>
          </thead>
          <tbody>
            {data.chart.map((c, i) => (
              <tr
                key={c.hours}
                data-active={i === active ? 'true' : undefined}
                data-mark={qBep !== null && Math.abs(c.hours - qBep) < 10 ? 'true' : undefined}
              >
                <th scope="row">{c.hours}</th>
                <td className="num">{formatNumber(c.revenue, 1)}</td>
                <td className="num">{formatNumber(c.total, 1)}</td>
                <td className="num strong">{formatNumber(c.result, 1, 0, true)}</td>
                <td className="num">{formatNumber(c.fixed, 1)}</td>
                <td className="num">{formatNumber(c.variable, 1)}</td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </ScrollRegion>
    </Layout>
  );
}
